import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Badge, statusTone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Input, Select } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RUN_STATUS_OPTIONS,
  RUN_TYPE_OPTIONS,
  RUN_WINDOW_OPTIONS,
  RUNS_LIST_IDLE_POLL_MS,
  RUNS_LIST_RUNNING_POLL_MS,
} from '@/constants/runs';
import { api } from '@/lib/api';
import { formatDuration, formatRelative, shortId } from '@/lib/format';
import type { WorkflowExecution } from '@/types/run';

export function RunsListPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [hours, setHours] = useState(24);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, type, status, hours, pageSize]);

  const runsQ = useQuery({
    queryKey: ['runs', 'paginated', debouncedQ, type, status, hours, page, pageSize],
    queryFn: () => {
      const params: {
        q?: string;
        type?: string;
        status?: string;
        last_hours?: number;
        page: number;
        page_size: number;
      } = {
        last_hours: hours,
        page,
        page_size: pageSize,
      };
      if (debouncedQ) params.q = debouncedQ;
      if (type !== 'all') params.type = type;
      if (status !== 'all') params.status = status;
      return api.listWorkflowsPaginated(params);
    },
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data?.items?.some((r) => r.status === 'RUNNING')) return RUNS_LIST_RUNNING_POLL_MS;
      return RUNS_LIST_IDLE_POLL_MS;
    },
    placeholderData: (previousData) => previousData,
  });

  const runs = useMemo(() => runsQ.data?.items ?? [], [runsQ.data]);
  const totalCount = runsQ.data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!runsQ.data) return;
    if (page > totalPages) setPage(totalPages);
  }, [runsQ.data, page, totalPages]);

  const columns: Column<WorkflowExecution>[] = [
    {
      key: 'workflow_id',
      header: 'Workflow',
      cell: (r) => <span className="font-mono text-xs">{shortId(r.workflow_id, 14, 4)}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      cell: (r) => <span className="text-xs">{r.workflow_type}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge>,
    },
    {
      key: 'duration',
      header: 'Duration',
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {formatDuration(r.start_time, r.close_time)}
        </span>
      ),
    },
    {
      key: 'started',
      header: 'Started',
      cell: (r) => (
        <span className="text-xs text-muted-foreground">{formatRelative(r.start_time)}</span>
      ),
    },
    {
      key: 'queue',
      header: 'Queue',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.task_queue}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Workflow runs</h1>
        <p className="text-sm text-muted-foreground">
          Backed by database + Temporal. {runs.length} of {totalCount} shown.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search workflow ID…"
          className="max-w-sm"
        />
        <Select value={type} onChange={(e) => setType(e.target.value)} className='max-w-[200px]'>
          {RUN_TYPE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className='max-w-[200px]'>
          {RUN_STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        <Select value={hours} onChange={(e) => setHours(Number(e.target.value))} className='max-w-[200px]'>
          {RUN_WINDOW_OPTIONS.map((w) => (
            <option key={w.value} value={w.value}>
              {w.label}
            </option>
          ))}
        </Select>
      </div>

      {runsQ.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : runsQ.isError ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          Failed to load runs. Is Temporal up? ({String(runsQ.error)})
        </div>
      ) : (
        <DataTable
          rows={runs}
          columns={columns}
          rowKey={(r) => r.workflow_id}
          onRowClick={(r) => {
            void navigate({ to: '/runs/$workflowId', params: { workflowId: r.workflow_id } });
          }}
          empty={
            <EmptyState
              icon={<Activity className="h-8 w-8" />}
              title="No runs in this window"
              description="Trigger a persona or content generation, then check back here."
            />
          }
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Page size</span>
            <Select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-[96px]"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
          </div>
          <div className="text-muted-foreground">
            Page {page} of {totalPages} · {totalCount} total
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={runsQ.isFetching || page <= 1}
          >
            Previous
          </Button>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={page}
            onChange={(e) => setPage(Math.min(totalPages, Math.max(1, Number(e.target.value) || 1)))}
            className="w-20"
          />
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={runsQ.isFetching || page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
